import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Loader2, X, Trophy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_OPEN = 'open-apply-modal';

/** Anywhere in the app: `openApplyModal()` opens the popup. */
export const openApplyModal = () => {
  window.dispatchEvent(new CustomEvent(EVENT_OPEN));
};

const empty = { athlete_name: '', email: '', sport: '', level: 'Pro', instagram: '', story: '' };
const LEVELS = ['Pro', 'College', 'High School', 'Draft', 'Rising'];

/**
 * ApplyToBeFeaturedModal — global popup that lets athletes submit the same
 * /api/applications form that lives on /athletes, without navigating away.
 * Mounted once in App.js; opened via `openApplyModal()` from any CTA.
 */
const ApplyToBeFeaturedModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = () => { setOpen(true); setDone(false); setError(''); };
    window.addEventListener(EVENT_OPEN, handler);
    return () => window.removeEventListener(EVENT_OPEN, handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const close = () => { setOpen(false); setForm(empty); setDone(false); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/applications`, form);
      try {
        const { track } = await import('../lib/analytics');
        track('athlete_application_submitted', {
          sport: form.sport,
          level: form.level,
          source: 'apply_modal',
        });
      } catch (_) { /* analytics never breaks UX */ }
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return createPortal(
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in"
      onClick={close}
      data-testid="apply-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b sticky top-0 bg-white">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">
              <Trophy className="w-4 h-4" />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">For athletes</p>
              <h2 className="font-display text-xl uppercase leading-tight">Apply to be Featured</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-gray-400 hover:text-black"
            data-testid="apply-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="apply-modal-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="font-display text-2xl uppercase mb-2">Got your pitch</h3>
            <p className="text-sm text-gray-600 max-w-sm mx-auto">
              We&apos;ll read every word. If your chapter is a fit, Jay will reach out within a few days.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-6 px-6 py-2.5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800 transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Your name *</label>
              <input required type="text" value={form.athlete_name} onChange={(e) => set('athlete_name', e.target.value)} placeholder="Your full name" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Sport *</label>
                <input required type="text" value={form.sport} onChange={(e) => set('sport', e.target.value)} placeholder="e.g. Football" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-sport" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Level *</label>
                <select required value={form.level} onChange={(e) => set('level', e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-level">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Instagram *</label>
              <input required type="text" value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="@yourhandle" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-instagram" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 block mb-1">Your story *</label>
              <textarea required rows={4} value={form.story} onChange={(e) => set('story', e.target.value)} placeholder="What chapter are you in right now? Why now?" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="apply-modal-story" />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 px-6 py-3 rounded-full bg-black text-white font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="apply-modal-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
              {submitting ? 'Sending' : 'Submit application'}
            </button>
            <p className="text-[10px] text-gray-400 text-center">We reply by email within 3-5 days.</p>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ApplyToBeFeaturedModal;
